import memoize from 'lodash.memoize';
import { Trans } from 'react-i18next';
import { ReactNode } from 'react';

type Validator<T> = (value: T) => string | ReactNode | undefined;

export function pipeValidators<T>(...validators: Validator<T>[]): Validator<T> {
	return (value: T): string | ReactNode | undefined =>
		validators.reduce(
			(error: string | ReactNode | undefined, validator: Validator<T>) => error || validator(value),
			undefined
		);
}

const createInputValidator = memoize((inputType: string) => {
	const input = document.createElement('input');
	input.type = inputType;
	input.required = true;

	return (value: string) => {
		input.value = value;
		return input.checkValidity();
	};
});

const isPlainObject = (value: string | null | undefined | object) =>
	Object.prototype.toString.call(value) === '[object Object]';

export const isVoid = (value: string | null | undefined | object) =>
	value == null || value === '' || value === undefined || isPlainObject(value);

const phoneNumberRegex =
	/^(\+?(420|421))? ?[1-9][0-9]{2} ?[0-9]{3} ?[0-9]{3}$|^\+?36[1-9][0-9]{7,8}$/;
export const phone = (value: string) => {
	return isVoid(value) || phoneNumberRegex.test(value) ? undefined : (
		<Trans i18nKey="forms.error.phone" />
	);
};

export const email = (value: string) =>
	value && !createInputValidator('email')(value) ? (
		<Trans i18nKey="forms.error.email" />
	) : undefined;

export const required = (value: string | undefined) =>
	isVoid(value) ? <Trans i18nKey="forms.error.required" /> : undefined;

export const checked = (value: boolean) => (value === true ? undefined : required(undefined));

export const url = (value: string) =>
	value && !createInputValidator('url')(value) ? <Trans i18nKey="forms.error.url" /> : undefined;
